const METADATA_ENVELOPE_KEYS = ['extracted_metadata', 'metadata'] as const;
const METADATA_ENVELOPE_KEY_SET = new Set<string>(METADATA_ENVELOPE_KEYS);

const SEARCH_RESULT_SYSTEM_METADATA_KEYS = new Set([
  'result_type',
  'result_id',
  'video_id',
  'video_uri',
  'video_name',
  'video_title',
  'title',
  'segment_id',
  'start_time',
  'end_time',
  'preview_segment_id',
  'preview_start_time',
  'preview_end_time',
  'preview_segment_uri',
  'preview_thumbnail_uri',
  'preview_gif_uri',
  'segment_uri',
  'gcs_uri',
  'thumbnail_gcs_uri',
  'gif_gcs_uri',
  'thumbnail_uri',
  'thumbnail_data',
  'thumbnail_available',
  'gif_uri',
  'gif_data',
  'gif_available',
  'text_content',
  'content_preview',
  'metadata_text',
  'similarity_score',
  'reranked_score',
  'fused_score',
  'text_score',
  'image_score',
  'text_rank',
  'image_rank',
  'match_type',
  'field_scores',
  'field_instance_scores',
  'matched_field_paths',
  'matched_field_instances',
  'matched_image_gcs_uri',
  'matched_image_uri',
  'matched_image_timestamp',
  'matched_image_score',
  'shot_timestamp',
  'run_id',
  'source_run_id',
  'prompt_run_id',
  'raw_llm_response',
  'source_index_id',
  'marker',
  'metadata_markers',
  'extracted_metadata_markers',
]);

const isRecordObject = (value: unknown): value is Record<string, any> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const parseMetadataCandidate = (value: unknown): Record<string, any> | undefined => {
  if (isRecordObject(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return isRecordObject(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const hasEntries = (value: Record<string, any>): boolean => Object.keys(value).length > 0;

const hasOnlySystemSiblingKeys = (value: Record<string, any>, envelopeKey: string): boolean => (
  Object.keys(value).every((key) => key === envelopeKey || SEARCH_RESULT_SYSTEM_METADATA_KEYS.has(key))
);

const unwrapMetadataEnvelopeOnce = (value: Record<string, any>): Record<string, any> => {
  const entries = Object.entries(value).filter(([, entryValue]) => entryValue !== undefined);

  if (entries.length === 1) {
    const [key, nestedValue] = entries[0];
    if (METADATA_ENVELOPE_KEY_SET.has(key) && isRecordObject(nestedValue)) {
      return nestedValue;
    }
  }

  for (const key of METADATA_ENVELOPE_KEYS) {
    const nestedValue = value[key];
    if (
      isRecordObject(nestedValue)
      && hasEntries(nestedValue)
      && hasOnlySystemSiblingKeys(value, key)
    ) {
      return nestedValue;
    }
  }

  return value;
};

export const normalizeSearchMetadataPayload = (value: unknown): Record<string, any> | undefined => {
  let candidate = parseMetadataCandidate(value);
  if (!candidate) {
    return undefined;
  }

  for (let depth = 0; depth < 4; depth += 1) {
    const nextCandidate = unwrapMetadataEnvelopeOnce(candidate);
    if (nextCandidate === candidate) {
      break;
    }
    candidate = nextCandidate;
  }

  return candidate;
};

export const selectSearchResultMetadata = (
  extractedMetadata: unknown,
  metadata: unknown,
): Record<string, any> => {
  const normalizedExtractedMetadata = normalizeSearchMetadataPayload(extractedMetadata);
  if (normalizedExtractedMetadata && hasEntries(normalizedExtractedMetadata)) {
    return normalizedExtractedMetadata;
  }

  const normalizedMetadata = normalizeSearchMetadataPayload(metadata);
  if (normalizedMetadata && hasEntries(normalizedMetadata)) {
    return normalizedMetadata;
  }

  return normalizedExtractedMetadata || normalizedMetadata || {};
};
